import { useMemo } from 'react';

import { Divider, Flex } from '@chakra-ui/react';
import { Button, ChevronDown, Dropdown, DropdownContent, DropdownItem, DropdownTrigger, Goal } from '@mesh.ai/rabbit';
import { useBulkUploadPanelAccess } from 'Deer/components/Goals/BulkUploadPanels/hooks/useBulkUploadPanelAccess';
import { useOKRBulkUploadPanel } from 'Deer/components/Goals/BulkUploadPanels/hooks/useOKRBulkUploadPanel';
import { useViewPageName } from 'components/Views/Hooks/useViewPageName';
import useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges from 'components/hooks/useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { checkBulkUploadIntentEnabled } from 'utils/helpers/featureFlags';

import { KeyResultWithoutPadding, ObjectiveWithoutPadding } from 'assets/assetsV1/iconComponents';

import { modifyString, StringModifier } from 'constants/copyHelpers';
import {
  sendClickedOnAddNewButton,
  sendClickedOnCreateKRevent,
  sendClickedOnCreateObjectiveEvent,
  sendOpenedKRPanelEvent,
  sendOpenedObjectivePanelEvent,
  sendPageViewEvent,
} from 'constants/mixpanelHelper';

import { TextBodyMedium, TextLabel } from 'uiToolkitV1/Typography';

export const AgGridAddNewGoalObjectiveSplitButton = ({
  onAddKRClick = () => {},
  variant = 'filled',
  onAddObjectiveClick = () => {},
}) => {
  const { objectivesName, keyResultsName } = useOrganisationSettings();

  /* Access Control for bulk upload objectives / key results starts here  */

  const { openBulkUploadPanel: openObjectiveBulkUploadPanel } = useOKRBulkUploadPanel({
    panelType: 'OBJECTIVES',
  });

  const { openBulkUploadPanel: openKeyResultBulkUploadPanel } = useOKRBulkUploadPanel({
    panelType: 'GOALS',
  });

  const page = useViewPageName();

  const { showBulkUploadObjectives, showBulkUploadKeyResults } = useBulkUploadPanelAccess();
  const showBulkUploadOKR = checkBulkUploadIntentEnabled();

  const {
    canCreateObjectiveAtRootLevel,

    canCreateObjectiveUnderKeyResult,
    canCreateObjectiveUnderObjective,
    canCreateKeyResultUnderKeyResult,
    canCreateKeyResultUnderObjective,
    canCreateKeyResultAtRootLevel,
  } = useOKRCreationPermissionsBasedOnEmployeeGoalsPrivileges();

  const canCreateObjective =
    canCreateObjectiveAtRootLevel || canCreateObjectiveUnderObjective || canCreateObjectiveUnderKeyResult;

  const canCreateKeyResult =
    canCreateKeyResultAtRootLevel || canCreateKeyResultUnderObjective || canCreateKeyResultUnderKeyResult;

  const addNewGoalOptions = useMemo(() => {
    const options = [];

    if (canCreateObjective)
      options.push({
        label: modifyString(objectivesName, StringModifier.TITLE_CASE),
        onClick: () => {
          sendClickedOnAddNewButton({
            eventProperties: {
              type: 'Obj',
              page,
            },
          });
          sendClickedOnCreateObjectiveEvent({
            eventProperties: { source: 'Add New Button', page },
          });
          sendOpenedObjectivePanelEvent({
            eventProperties: {
              source: 'Add New Button',
              page,
              type: 'Add Objective Panel',
            },
          });
          sendPageViewEvent({
            eventProperties: {
              source: 'Add New Button',
              page: 'Add Objective Panel',
              type: 'Panel',
            },
          });
          onAddObjectiveClick();
        },
        Icon: ObjectiveWithoutPadding,
        disabled: false,
      });

    if (canCreateKeyResult) {
      options.push({
        label: modifyString(keyResultsName, StringModifier.TITLE_CASE),
        onClick: () => {
          sendClickedOnAddNewButton({
            eventProperties: {
              type: 'KR',
              page,
            },
          });
          sendClickedOnCreateKRevent({
            eventProperties: { source: 'Add New Button', page },
          });
          sendOpenedKRPanelEvent({
            eventProperties: {
              source: 'Add New Button',
              page,
              type: 'Add KR Panel',
            },
          });
          sendPageViewEvent({
            eventProperties: {
              source: 'Add New Button',
              page: 'Add KR Panel',
              type: 'Panel',
            },
          });
          onAddKRClick();
        },
        Icon: KeyResultWithoutPadding,
        disabled: false,
      });
    }

    return options;
  }, [canCreateKeyResult, canCreateObjective, keyResultsName, objectivesName, onAddKRClick, onAddObjectiveClick, page]);

  const bulkUploadOptions = useMemo(() => {
    const options = [];
    // Show bulk upload intent if enabled instead of showing individual bulk upload options
    if (showBulkUploadOKR) {
      return options;
    }
    if (showBulkUploadObjectives) {
      options.push({
        label: modifyString(objectivesName, StringModifier.TITLE_CASE),
        onClick: () => {
          sendClickedOnAddNewButton({
            eventProperties: {
              type: 'Bulk Upload Objectives',
              page,
            },
          });
          sendPageViewEvent({
            eventProperties: {
              source: 'Add New Button',
              page: 'Bulk Upload Objective Panel',
              type: 'Panel',
            },
          });
          openObjectiveBulkUploadPanel();
        },
        Icon: ObjectiveWithoutPadding,
        disabled: false,
      });
    }

    if (showBulkUploadKeyResults) {
      options.push({
        label: modifyString(keyResultsName, StringModifier.TITLE_CASE),
        onClick: () => {
          sendClickedOnAddNewButton({
            eventProperties: {
              type: 'Bulk Upload KRs',
              page,
            },
          });
          sendPageViewEvent({
            eventProperties: {
              source: 'Add New Button',
              page: 'Bulk Upload KR Panel',
              type: 'Panel',
            },
          });
          openKeyResultBulkUploadPanel();
        },
        Icon: KeyResultWithoutPadding,
        disabled: false,
      });
    }

    return options;
  }, [
    keyResultsName,
    objectivesName,
    openKeyResultBulkUploadPanel,
    openObjectiveBulkUploadPanel,
    page,
    showBulkUploadKeyResults,
    showBulkUploadObjectives,
  ]);

  if (!addNewGoalOptions.length && !bulkUploadOptions.length) return null;

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {addNewGoalOptions.length > 1 ? (
        <Dropdown>
          <DropdownTrigger asChild>
            <Button data-testid="add-okr-button" variant={variant} iconLeft={Goal} iconRight={ChevronDown}>
              Add
            </Button>
          </DropdownTrigger>
          <DropdownContent
            style={{
              padding: '16px 8px',
              minWidth: '180px',
            }}
            align="start"
          >
            {bulkUploadOptions.length > 0 && (
              <TextLabel ml="8px" mb="4px" bold color="text.g2">
                ADD SINGLE
              </TextLabel>
            )}
            {addNewGoalOptions.map((option) => (
              <DropdownItem
                onSelect={() => {
                  option.onClick();
                }}
              >
                <Flex key={option.label} alignItems="center">
                  <option.Icon />
                  <TextBodyMedium ml="8px">{option.label}</TextBodyMedium>
                </Flex>
              </DropdownItem>
            ))}
            {bulkUploadOptions.length > 0 && (
              <>
                <Divider my="8px" background="#CFD7DF" opacity={1} />
                <TextLabel ml="8px" mb="4px" bold color="text.g2">
                  BULK CREATE
                </TextLabel>
              </>
            )}
            {bulkUploadOptions.map((option) => (
              <DropdownItem
                onSelect={() => {
                  option.onClick();
                }}
              >
                <Flex key={option.label} alignItems="center">
                  <option.Icon />
                  <TextBodyMedium ml="8px">{option.label}</TextBodyMedium>
                </Flex>
              </DropdownItem>
            ))}
          </DropdownContent>
        </Dropdown>
      ) : (
        <Button iconLeft={Goal} onClick={addNewGoalOptions[0].onClick} variant={variant}>
          Add New {addNewGoalOptions[0].label}
        </Button>
      )}
    </>
  );
};
